import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, RotateCcw, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { fmtMT } from "@/lib/mountainTime";
import { LiveCtx, LiveAuction } from "@/components/pipeline/LiveAuction";
import { money, errText } from "@/lib/bidCenterApi";

const CARD = { ids: { horse_id: "SIM" } };

function BidTable({ bids }) {
  return (
    <div className="overflow-x-auto rounded-lg border bg-white">
      <table data-testid="sim-bids" className="w-full text-xs">
        <thead className="bg-stone-50 text-stone-500"><tr><th className="p-2 text-left">Bidder</th><th className="p-2 text-left">Amount</th><th className="p-2 text-left">Placed (Facebook)</th><th className="p-2 text-left">Synced</th><th className="p-2 text-left">Engine</th></tr></thead>
        <tbody>{bids.map((b) => (
          <tr key={b.id} data-testid={`sim-bid-${b.id}`} className="border-t"><td className="p-2">{b.author}</td><td className="p-2">{money(b.amount, "CAD")}</td><td className="p-2 whitespace-nowrap">{fmtMT(b.placed)}</td>
            <td className={`p-2 whitespace-nowrap ${b.synced !== b.placed ? "text-[#92400E]" : ""}`}>{fmtMT(b.synced)}</td><td className="p-2">{b.state}{b.issues.length ? ` · ${b.issues.join(", ")}` : ""}</td></tr>))}
          {!bids.length && <tr><td colSpan={5} className="p-2 text-stone-500">No bids synced yet.</td></tr>}</tbody>
      </table>
    </div>
  );
}

export default function AuctionSimulator() {
  const nav = useNavigate();
  const [i, setI] = useState(0);
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback((n) => {
    const t0 = Date.now();
    api.get(`/admin/bid-center/simulator/${n}`).then((r) => { setD({ ...r.data, offset: new Date(r.data.server_now).getTime() - (t0 + Date.now()) / 2 }); setErr(null); })
      .catch((e) => { setErr(errText(e)); toast.error(errText(e)); });
  }, []);
  useEffect(() => { load(i); }, [i, load]);
  const live = d ? { items: { SIM: d.item }, offset: d.offset, refresh: () => load(i), loading: false, error: false } : null;
  return (
    <div className="min-h-screen bg-[#F7F5F2]">
      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <button data-testid="sim-back" onClick={() => nav("/bids")} className="text-sm flex items-center gap-1 text-stone-600"><ArrowLeft className="h-4 w-4" />Bid Management</button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2"><FlaskConical className="h-6 w-6" />Auction Simulator</h1>
          <p className="text-sm text-stone-600">Preview only. A fictional auction run through the real bid engine, soft-close rules and Pipeline card on a virtual clock. Nothing is saved, sent or posted.</p>
        </div>
        {err && <p data-testid="sim-error" className="text-sm text-red-700">{err}</p>}
        {d && <>
          <div className="flex items-center gap-2">
            <Button data-testid="sim-prev" variant="outline" disabled={i === 0} onClick={() => setI(i - 1)} className="h-11"><ChevronLeft className="h-4 w-4" />Previous</Button>
            <Button data-testid="sim-next" disabled={i >= d.total - 1} onClick={() => setI(i + 1)} className="h-11 flex-1">Next<ChevronRight className="h-4 w-4" /></Button>
            <Button data-testid="sim-restart" variant="outline" onClick={() => setI(0)} className="h-11"><RotateCcw className="h-4 w-4" /></Button>
          </div>
          <div className="rounded-xl border bg-white p-4 space-y-1">
            <p data-testid="sim-step" className="text-xs font-semibold text-stone-500">Step {d.step + 1} of {d.total}</p>
            <h2 data-testid="sim-title" className="text-lg font-bold">{d.title}</h2>
            <p data-testid="sim-expect" className="text-sm text-stone-700">Expected: {d.expect}</p>
            <p data-testid="sim-clock" className="text-xs text-stone-500">Simulated time {fmtMT(d.server_now)} · scheduled close {fmtMT(d.item.soft_close.scheduled_close)} · phase <b>{d.item.phase}</b></p>
          </div>
          <div data-testid="sim-card" className="rounded-xl border bg-white p-3 max-w-sm">
            <p className="font-semibold text-sm">SIM Fictional Mare</p>
            <p className="text-[11px] text-stone-600">Fictional seller · Ongoing</p>
            <LiveCtx.Provider value={live}><LiveAuction key={d.step} c={CARD} testId="sim-card" /></LiveCtx.Provider>
          </div>
          <BidTable bids={d.bids} />
        </>}
      </main>
    </div>
  );
}
